/**
 * Persists how the user responded to each HeyBodhi "session mirror" insight (This resonates /
 * Not quite / Still sitting with this). Responses are keyed per user in localStorage until a
 * Supabase-backed insights pipeline exists.
 */
export type MirrorInsightFeedback = "resonates" | "not_quite" | "sitting";

export type SessionMirrorInsightRecord = {
  insightId: string;
  feedback: MirrorInsightFeedback;
  /** ISO timestamp */
  recordedAt: string;
};

const STORAGE_PREFIX = "heybodhi:session-mirror-responses:v1:";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readAll(userId: string): SessionMirrorInsightRecord[] {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is SessionMirrorInsightRecord =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as SessionMirrorInsightRecord).insightId === "string" &&
        typeof (x as SessionMirrorInsightRecord).recordedAt === "string" &&
        (["resonates", "not_quite", "sitting"] as const).includes(
          (x as SessionMirrorInsightRecord).feedback
        )
    );
  } catch {
    return [];
  }
}

function writeAll(userId: string, rows: SessionMirrorInsightRecord[]): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(rows));
  } catch {
    /* quota / private mode */
  }
}

export function listMirrorInsightResponses(userId: string): SessionMirrorInsightRecord[] {
  return readAll(userId);
}

/** One response per insight id; duplicates are ignored (idempotent). */
export function recordMirrorInsightResponse(
  userId: string,
  insightId: string,
  feedback: MirrorInsightFeedback
): void {
  if (!userId || !insightId) return;
  const rows = readAll(userId);
  if (rows.some((r) => r.insightId === insightId)) return;
  rows.push({
    insightId,
    feedback,
    recordedAt: new Date().toISOString(),
  });
  writeAll(userId, rows);
}

export function answeredInsightIds(userId: string): Set<string> {
  return new Set(readAll(userId).map((r) => r.insightId));
}
